import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2 } from 'lucide-react'
import { OrgChartService, RoleDefinition } from '../../services/orgChartService'

interface EditRoleDefinitionModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called after create/update/delete with the active definition id (null if deleted). */
  onSaved: (definitionId: string | null) => Promise<void> | void
  /** Existing definition to edit, or null to create. */
  definition: RoleDefinition | null
}

export const EditRoleDefinitionModal: React.FC<EditRoleDefinitionModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  definition
}) => {
  const [name, setName] = useState('')
  const [accountabilities, setAccountabilities] = useState<string[]>([])
  const [isContractor, setIsContractor] = useState(false)
  const [newAccountability, setNewAccountability] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const isEditing = !!definition
  const orgChartService = OrgChartService.getInstance()

  useEffect(() => {
    if (definition) {
      setName(definition.name)
      setAccountabilities(definition.accountabilities || [])
      setIsContractor(!!definition.isContractor)
    } else {
      setName('')
      setAccountabilities([])
      setIsContractor(false)
    }
    setNewAccountability('')
    setError('')
  }, [definition, isOpen])

  const handleAddAccountability = () => {
    if (newAccountability.trim()) {
      setAccountabilities([...accountabilities, newAccountability.trim()])
      setNewAccountability('')
    }
  }

  const handleRemoveAccountability = (index: number) => {
    setAccountabilities(accountabilities.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Role name is required')
      return
    }

    try {
      setSaving(true)
      setError('')

      if (isEditing && definition) {
        await orgChartService.updateRoleDefinition(definition.id, {
          name: name.trim(),
          accountabilities,
          isContractor
        })
        await onSaved(definition.id)
      } else {
        const id = await orgChartService.createRoleDefinition({
          name: name.trim(),
          accountabilities,
          isContractor
        })
        await onSaved(id)
      }
    } catch (err: any) {
      console.error('Error saving role definition:', err)
      setError(err.message || 'Failed to save role')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateAsNew = async () => {
    if (!name.trim()) {
      setError('Role name is required')
      return
    }

    try {
      setSaving(true)
      setError('')

      const id = await orgChartService.createRoleDefinition({
        name: name.trim(),
        accountabilities,
        isContractor
      })
      await onSaved(id)
    } catch (err: any) {
      console.error('Error creating role definition:', err)
      setError(err.message || 'Failed to create role')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!definition) return

    if (!confirm('Delete this role? Org chart cards using it must be reassigned first.')) {
      return
    }

    try {
      setSaving(true)
      setError('')
      await orgChartService.deleteRoleDefinition(definition.id)
      await onSaved(null)
    } catch (err: any) {
      console.error('Error deleting role definition:', err)
      setError(err.message || 'Failed to delete role')
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Role' : 'Create Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Senior Software Engineer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Accountabilities
            </label>

            {accountabilities.length > 0 && (
              <ul className="mb-2 space-y-1">
                {accountabilities.map((acc, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded"
                  >
                    <span className="text-sm text-gray-700">{acc}</span>
                    <button
                      onClick={() => handleRemoveAccountability(idx)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex space-x-2">
              <input
                type="text"
                value={newAccountability}
                onChange={(e) => setNewAccountability(e.target.value)}
                placeholder="Add an accountability..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddAccountability()
                  }
                }}
              />
              <button
                onClick={handleAddAccountability}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700">
                Contractor
              </label>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isContractor}
              onClick={() => setIsContractor(v => !v)}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                isContractor ? 'bg-sky-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${
                  isContractor ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div>
            {isEditing && (
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors text-sm"
              >
                Delete Role
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {isEditing && (
              <button
                onClick={handleCreateAsNew}
                disabled={saving}
                className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Create as New Role'}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
